import { ScriptParamsProvider } from '../lib/ScriptUrlParser';

import * as userIdProviderManager from './UserIdProviders/UserIdProviderManager';
import * as localUserIdProvider from './UserIdProviders/LocalUserIdProvider';
import { ServerSideUserIdProvider } from './UserIdProviders/ServerSideUserIdProvider';
import * as eventSourceManger from './EventSources/EventSourceManager';
import * as pageViewSource from './EventSources/PageViewSource';
import * as urlSourceChange from './EventSources/URLSourceChange';

import * as actionHandler from './ActionHandler/ActionHandler';
import * as overlayActionHandler from './ActionHandler/OverlayActionHandlerProxy';
import * as cookieActionHandler from './ActionHandler/CookieActionHandler';
import * as injectHTMLActionHandler from './ActionHandler/InjectHTMLActionHandler';
import * as injectScriptActionHandler from './ActionHandler/InjectScriptActionHandler';
import * as pixelActionHandler from './ActionHandler/PixelActionHandler';

import { ActionHandlerManager } from './ActionHandler/ActionHandlerManager';

import { ILogger, Logger } from '../lib/logger';

import * as jscookie from "js-cookie";
import { isDealer } from './DealerRecognition/DealerRecognizer';
import { HandoverHandlerManager } from './ActionHandler/HandoverHandlerManager';
import { ClientApi, TrackEventData, TrackingResultFinal, UserId, UserIdProviderType, DataExchangeType, ActionType, FinalIssuedAction, UnissuedActionBase, IssuedOverlayAction, ExternalCookieModel, IssuedInjectScriptAction, ClientSampling, DebuggerEventType, UserCookieModel, LogContext, SeverityLogLevel, LogType, VwFreespeeUserDataModel, ExternalActionClientModel } from '../api/client/clientApi';
import { DebuggerState, IEngine, IProfileDebuggerData, ENGINE_VERSION } from './IEngine';
import { IApiCommandCallback, IApiCommandResult, IGetUserIdResult, ITrackingClient, ITrackingEvent, TrackingClientEventName } from './ITrackingClient.public';
import { ProfileDebuggerBootstrap } from '../SmartSignalsProfileDebugger/profile-debugger-bootstrap';
import { RequestType, ApiRequestResponseData } from '../SmartSignalsProfileDebugger/types/TrackingRequestResponseData';
import { InjectScriptActionHandler } from './ActionHandler/InjectScriptActionHandler';
import { envConfig } from "../resources/env-config";
import { ApiPromise } from '../api/client/clientApiExtension';
import { Deferred } from '../lib/utils';
import { CollectionActionGroupsProvider } from './ActionHandler/CollectionActionGroupsProvider/CollectionActionGroupsProvider';
import type { VwFreespeeModule } from './Modules/vwFreespee';
import { UpdatedStateDebuggerData } from '../SmartSignalsProfileDebugger/resources/models/UpdatedStateDebuggerData';

declare var __webpack_require__: any;

export class Engine implements ITrackingClient {
    private static readonly UidTokenPrefix: string = 'smartSignals2UiD';
    private static readonly Version: string = ENGINE_VERSION //'202504300859';
    private static readonly ApiCommands = ["getUserId", "getPermanentUserId"];
    private static readonly AdobeCookieName = 'AMCV_1D59171B520E91320A490D44@AdobeOrg';
    private static readonly OneHour = 3.6e+6;

    private static readonly isFunction = (obj: any): boolean => typeof obj === "function";

    public readonly scopeName: string = 'SmartSignals2';

    private readonly _collectionActionGroupsProvider: CollectionActionGroupsProvider = new CollectionActionGroupsProvider();

    private static ProfileDebuggerKey: string = 'profileDebugger';

    private _params!: { [index: string]: string | undefined; };
    private _conf!: IConfiguration;
    private _eventFunctions: Map<string, { (eventType: string, data: IEventData): void }[]> = new Map<string, { (eventType: string, data: IEventData): void }[]>();
    private _userIdProviderManager: userIdProviderManager.UserIdProviderManager;
    private _localUserIdProvider: localUserIdProvider.LocalUserIdProvider;
    private _eventSourceManger: eventSourceManger.EventSourceManager;
    private _userIds: UserId[] | undefined;
    private _excludedUser: boolean = false;
    private _initDone: boolean = false;
    private _clientApi: ClientApi;
    private _actionHandlerManager = new ActionHandlerManager();
    private _handoverHandlerManager = new HandoverHandlerManager();
    private _hideActionsCounter = 0;
    private _cachedResoveUserIdsPromise: Promise<UserId[]> | undefined;
    private _initQueue: Array<ITrackingEvent> = new Array<ITrackingEvent>();
    private _engine!: IEngine;

    private _debuggerApiCallsQueue: ApiRequestResponseData[] | undefined = [];
    private _debuggerEnabling = true;
    private _debuggerReady = false;
    private _profileDebuggerBootstrap?: ProfileDebuggerBootstrap;
    private _logger: Logger;
    private _realUserIdPromise = new Deferred<string>();
    private _modules: Map<string, Promise<any>> = new Map<string, Promise<any>>();

    public get log(): ILogger {
        return this._logger.external;
    }

    constructor(initQueue?: Array<ITrackingEvent>) {
        this._initialize();
        this._clientApi = new ClientApi(this._conf.rtdeUrl);

        this._setupCommunicationChannel();
        this._logger = new Logger(this._clientApi, () => this._getLoggerContext());
        this._logger.setListener();

        if (initQueue) {
            if (Array.isArray(initQueue) && initQueue.length > 0)
                this._initQueue.push.apply(this._initQueue, initQueue);
        }

        this._userIdProviderManager = new userIdProviderManager.UserIdProviderManager();
        this._localUserIdProvider = new localUserIdProvider.LocalUserIdProvider("LocalUserId", Engine.UidTokenPrefix, true);
        this._eventSourceManger = new eventSourceManger.EventSourceManager();


        this._registerCookieProviders();
        if (this._conf.nativeEventTracking)
            this._registerEventSources();
        this._registerActionHanlers();

        this._recognizeDealer().then(dealer => {
            this._excludedUser = dealer;
            if (this._excludedUser == false) {
                this._resolveUserId().then(userIds => {
                    this._userIds = userIds;
                    this._initDone = true;
                    this.push.apply(this, this._initQueue);
                    this._initQueue.length = 0;
                });
            }
        });
        if (this._engine.conf.smasiProfileDebuggerEnabled) {
            this._detectProfileDebuggerRequest();
            window.addEventListener('hashchange', () => {
                this._detectProfileDebuggerRequest();
            }, false);
        } else {
            this.notifyDebuggerDisabled();
        }

        // migration: remove incorrect httpOnly cookie & recreate javascript cookie
        if (this._params["engineCNameUrl"] && this._removeSlash(this._params["engineCNameUrl"]) == this._conf.rtdeUrl) {
            if (this._localUserIdProvider.isSupported() && !this._localUserIdProvider.readCookie()) {
                const clientApi = new ClientApi(this._conf.rtdeUrl, true);
                clientApi.userCookie_Delete_3(this._conf.cookieName, true)
                    .then(value => {
                        if (value) {
                            this._localUserIdProvider.persist();
                        }
                    });
            }
        }
    }

    public static get smasiProfileDebuggerDefaultHeight() {
        return Math.min(0.25 * document.documentElement.clientHeight, 170);
    }

    private _getProfileDebuggerConfig(): IProfileDebuggerData {
        try {
            let config = this._getConfiguration<IProfileDebuggerData>(Engine.ProfileDebuggerKey);
            config = {
                s: config?.s || 0,
                ht: config?.ht || Engine.smasiProfileDebuggerDefaultHeight
            };
            if (config.ht > document.documentElement.clientHeight)
                config.ht = Math.max(document.documentElement.clientHeight - 10, 20);

            return config;
        } catch (ignored) {
            return {
                s: 0,
                ht: Engine.smasiProfileDebuggerDefaultHeight
            };
        }
    }

    private _setProfileDebuggerState(state: number, height?: number) {
        const config = this._getProfileDebuggerConfig();

        config.s = state;
        config.ht = height || config.ht || Engine.smasiProfileDebuggerDefaultHeight;

        this._setConfiguration(Engine.ProfileDebuggerKey, config);
    }

    private _setConfiguration<T>(key: string, config: T) {
        try {
            localStorage.setItem('smasi2-' + key, JSON.stringify(config));
            localStorage.removeItem('smasi2ProfileDebuggerState');
            localStorage.removeItem('smasi2ProfileDebuggerHeight');
        } catch (ignored) {
            // ignore local storage errors
        }
    }

    private _getConfiguration<T>(key: string): T | undefined {
        try {
            let value = localStorage.getItem('smasi2-' + key);
            if (!value)
                return undefined;
            return JSON.parse(value)
        } catch (ignored) {
            return undefined;
        }
    }

    private _detectProfileDebuggerRequest(): boolean {
        const debuggerStatus = this._getDebuggerState();
        let config = this._getProfileDebuggerConfig();

        const skipLog = (debuggerStatus == undefined)
            || (debuggerStatus == DebuggerState.ON && config.s == 1)
            || (debuggerStatus == DebuggerState.MIN && config.s == 2)
            || (debuggerStatus == DebuggerState.OFF && config.s == 0);

        if (debuggerStatus !== undefined) {
            this._setProfileDebuggerState(debuggerStatus);
            if (!debuggerStatus) {
                if (this._profileDebuggerBootstrap)
                    this._profileDebuggerBootstrap.removeDebugger();
                else {
                    if (!skipLog)
                        this._debuggerEvent(DebuggerEventType.Closed);
                }
            }
        }
        config = this._getProfileDebuggerConfig();

        if (config.s > 0) {
            this.notifyDebuggerEnabling();
            this._showProfileDebugger(config, skipLog);
            return true;
        }

        this.notifyDebuggerDisabled();
        return false;
    }

    private async _showProfileDebugger(config: IProfileDebuggerData, skipLogShow: boolean): Promise<void | ProfileDebuggerBootstrap> {
        return import(/* webpackChunkName: "smasi2ProfileDebuggerBootstrap" */ '../SmartSignalsProfileDebugger/profile-debugger-bootstrap').then(bootstrap => {
            if (!this._profileDebuggerBootstrap) {
                this._profileDebuggerBootstrap = new bootstrap.ProfileDebuggerBootstrap(this._engine, this._logger);
            }
            this._profileDebuggerBootstrap.showDebugger(config, skipLogShow);
            return this._profileDebuggerBootstrap;
        }, reason => {
            console.error("failed loading debugger bootstrap: " + reason);
        });
    }

    private _removeSlash(url?: string): string | undefined {
        if (!url)
            return url;

        if (url.charAt(url.length - 1) == '/')
            return url.substr(0, url.length - 1);
        return url;
    }

    private _ensureSlash(url: string): string {
        if (url.charAt(url.length - 1) != '/')
            return url + '/';
        return url;
    }

    private _initialize() {
        const scriptParamsProvider = new ScriptParamsProvider();

        let params = scriptParamsProvider.flatten();
        this._params = params;

        function parseParameterBool(name: string): boolean | undefined {
            let value = params[name];

            if (value !== undefined && value !== null) {
                value = value.toLowerCase();
                if (value === 'true')
                    return true;
                if (value === 'false')
                    return false;
            }
            return undefined;
        }

        function parseParameter(name: string): [boolean?, string?] {
            const bool = parseParameterBool(name);
            if (typeof bool === 'boolean')
                return [bool, undefined];
            return [undefined, params[name]];
        }

        if (__webpack_require__ && __webpack_require__.p)
            __webpack_require__.p = this._ensureSlash(scriptParamsProvider.origin + scriptParamsProvider.path);
        //__webpack_require__.p = preprocessUrl2(scUrlParser.origin + __webpack_require__.p);

        let adobeIdCookie: string | undefined;
        const parsedAdobeIdCookie = parseParameter("adobeIdCookie");
        if (parsedAdobeIdCookie[0] === true)
            adobeIdCookie = Engine.AdobeCookieName;
        else if (parsedAdobeIdCookie[0] === undefined)
            adobeIdCookie = parsedAdobeIdCookie[1];

        const rtdeUrl = this._removeSlash(params["rtdeUrl"] || params["engine1stPartyUrl"] || params["engineCNameUrl"] || params["engineUrl"] || scriptParamsProvider.origin)!;
        const cookieName = params['cookieName'] || "smartSignals2UiD";

        let xdmRecognitionParsed = parseParameter("xdmRecognition");
        let serverCookieParsed = parseParameter("serverCookie");

        this._conf = {
            jsAlias: params["jsAlias"],

            rtdeUrl: rtdeUrl,

            cookieName: cookieName,
            serverCookieName: params['serverCookieName'] || this._getDefaultServerCookieName(cookieName),

            xdmRecognitionUrl: xdmRecognitionParsed[0] === true ? this._removeSlash(params["engineUrl"] || rtdeUrl) : this._removeSlash(xdmRecognitionParsed[1]),
            serverCookieUrl: serverCookieParsed[0] === true ? rtdeUrl : (serverCookieParsed[0] === false ? undefined : this._removeSlash(serverCookieParsed[1] || params["engine1stPartyUrl"] || params["engineCNameUrl"])),

            scriptId: params["scriptId"]!,
            nativeEventTracking: parseParameterBool("nativeEventTracking") !== false,
            dealerRecognition: parseParameterBool("dealerRecognition") === true,
            cleanUpOnURLChange: parseParameterBool("cleanUpOnURLChange") === true,
            smasiProfileDebuggerEnabled: parseParameterBool("smasiProfileDebuggerEnabled") !== false,

            adobeIdCookie: adobeIdCookie,
            adobePartnerId: params["adobePartnerId"],
            adobePartnerSystem: params["adobePartnerSystem"]
        };

        if (this._conf.xdmRecognitionUrl && this._conf.serverCookieUrl && this._conf.xdmRecognitionUrl == this._conf.serverCookieUrl)
            this._conf.xdmRecognitionUrl = undefined;

        if (this._conf.jsAlias) {
            let current = (<any>window)[this._conf.jsAlias];

            if (!current) {
                (<any>window)[this._conf.jsAlias] = this;
            }
            else if (!isEngine(current)) {
                console.warn("'jsAlias' conflicts with existing variable");
            }
        }

        this._engine = {
            conf: this._conf,
            getActionHandler: this._getActionHandler.bind(this),
            getHandoverHandler: this._getHandoverHandler.bind(this),
            trigger: this._trigger.bind(this),
            unissuedAction: this._unissuedAction.bind(this),
            getCurrentUrl: this._getCurrentUrl.bind(this),
            debuggerEvent: this._debuggerEvent.bind(this),
            getClientSampling: this._getClientSampling.bind(this),
            getCrossDomainCookie: this._getCrossDomainCookie.bind(this),
            getUserId: this._getUserId.bind(this, false),
            setProfileDebuggerState: this._setProfileDebuggerState.bind(this),
            getProfileDebuggerConfig: this._getProfileDebuggerConfig.bind(this),
            getConfiguration: this._getConfiguration.bind(this),
            setConfiguration: this._setConfiguration.bind(this),
            debuggerReady: this.debuggerReady.bind(this),
            trackApiCall: this.trackApiCall.bind(this),
            env: envConfig.environment,
            sendHandlingState: this._sendHandlingState.bind(this)
        }
    }

    private _getDefaultServerCookieName(cookieName: string) {
        const suffix = "UiD";

        if (cookieName.endsWith(suffix)) {
            // Insert 's' before the suffix
            return cookieName.slice(0, -suffix.length) + 's' + suffix;
        }
        // Append 's' to the end
        return cookieName + 's';
    }

    private _recognizeDealer(): Promise<boolean> {
        if (this._conf.dealerRecognition) {
            if (!this._localUserIdProvider.isDealer) {
                return isDealer().then(value => this._localUserIdProvider.isDealer = value);
            } else {
                return Promise.resolve(true);
            }
        }
        return Promise.resolve(false);
    }

    public on(eventName: TrackingClientEventName, fn: (eventName: string, data: IEventData) => void): void {
        if (!eventName || !fn)
            return;

        const arrFn = this._eventFunctions.get(eventName);
        if (!arrFn) {
            this._eventFunctions.set(eventName, new Array(fn));
        } else if (!arrFn.includes(fn)) {
            arrFn.push(fn);
            this._eventFunctions.set(eventName, arrFn);
        }
    }

    public off(eventName: TrackingClientEventName, fn: (eventName: string, data: IEventData) => void): void {
        if (!eventName || !fn)
            return;

        const arrFn = this._eventFunctions.get(eventName);
        if (!arrFn)
            return;

        const index = arrFn.indexOf(fn);
        if (index === -1)
            return;

        arrFn.splice(index, 1);
        this._eventFunctions.set(eventName, arrFn);
    }

    private _trigger(eventName: TrackingClientEventName, data: any): void {
        let ts = this._eventFunctions.get(eventName);
        if (!ts?.length)
            return;
        let triggers = [...ts];
        triggers.forEach((func) => {
            try {
                func(eventName, data);
            } catch (e) { }
        })
    }

    private _resolveUserId(useCachedPromise: boolean = false): Promise<UserId[]> {
        if (useCachedPromise && this._cachedResoveUserIdsPromise) {
            return this._cachedResoveUserIdsPromise;
        } else {
            return this._cachedResoveUserIdsPromise = this._resolveUserIdInternal();
        }
    }

    private async _resolveUserIdInternal(): Promise<UserId[]> {
        const skipXdm = new Date().valueOf() - this._localUserIdProvider.lastXdmCheck < Engine.OneHour * 24

        const userIdTmp: Promise<UserId>[] = [];

        for (const [userIdProviderName, provider] of Object.entries(this._userIdProviderManager.getProviders())) {

            if (userIdProviderName === "XdmUserId") {
                if (skipXdm)
                    continue;
                this._localUserIdProvider.lastXdmCheck = new Date().valueOf();
            }

            userIdTmp.push(provider.getUserId());
        }

        var userIds = await Promise.all(userIdTmp);
        var userId = this._pickUserId(userIds);

        if (userId?.indexOf("*tmp*") == -1)
            this._realUserIdPromise.resolve(userId);

        return userIds;
    }

    private _registerCookieProviders() {
        this._userIdProviderManager.register("LocalUserId", this._localUserIdProvider);
        if (this._conf.xdmRecognitionUrl)
            this._userIdProviderManager.register("XdmUserId", new ServerSideUserIdProvider(this._conf.xdmRecognitionUrl, this._conf.cookieName, "XdmUserId", this._engine));
        if (this._conf.serverCookieUrl)
            this._userIdProviderManager.register("ServerSideLocalUserId", new ServerSideUserIdProvider(this._conf.serverCookieUrl, this._conf.serverCookieName, "ServerSideLocalUserId", this._engine));
    }

    private _registerEventSources() {
        this._eventSourceManger.register("PageView", new pageViewSource.PageViewSource(this));
        this._eventSourceManger.register("URLSourceChange", new urlSourceChange.URLSourceChange(this));
    }

    public getPermanentUserId(callback: IApiCommandCallback<IGetUserIdResult>): boolean {
        return this.getUserId(callback, false);
    }

    public getUserId(callback: IApiCommandCallback<IGetUserIdResult>, getTempUids: boolean = true): boolean {
        if (!callback || !Engine.isFunction(callback.onready))
            return false;

        this._getUserId(getTempUids).then(userId => callback.onready({ userId: userId, success: !!userId }));

        return true;
    }

    private _getUserId(getTempUids: boolean = true): Promise<string | undefined> {
        return this._resolveUserId(true).then(async uids => {
            var userId: string | undefined;
            if (uids && uids.length) {
                var realIds = uids.filter(i => i.id && (getTempUids || i.id.indexOf("*tmp*") == -1));
                if (realIds.length) {
                    userId = this._pickUserId(realIds);
                }
            }
            if (!userId && !getTempUids)
                userId = await this._realUserIdPromise.promise;

            return userId;
        });
    }

    private _pickUserId(userIds: UserId[]) {
        let userId: string | undefined;

        var crossIds = userIds.filter(i => i.type == UserIdProviderType.CrossDomain);
        if (crossIds.length) {
            userId = crossIds[0].id;
        } else {
            userId = userIds[0].id;
        }
        return userId;
    }

    private async _debuggerEvent(et: DebuggerEventType) {
        const userId = await this._getUserId(false);
        if (!userId)
            return;
        this._clientApi.profileDebuggerEvent_Put_2(
            this._conf.scriptId,
            userId,
            {
                url: window.location.href,
                debuggerEventType: et
            }
        );
    }

    private _getCrossDomainCookie(): Promise<UserCookieModel | null> {
        const provider = this._userIdProviderManager.getProviderByName("XdmUserId") as ServerSideUserIdProvider;

        if (!provider)
            return Promise.resolve(null);
        return provider.getCrossDomainCookie();
    }

    public push(...items: ITrackingEvent[]): number {
        if (this._excludedUser || items.length === 0)
            return 0;
        for (let i = 0; i < items.length; i++)
            this._pushItem(items[i]);
        return items.length;
    }

    public getScriptActionTriggerInfo(actionId: string) {
        const scriptActionHandler = this._actionHandlerManager.get(ActionType.Script) as InjectScriptActionHandler;
        return scriptActionHandler.getTriggeredAction(actionId);
    }

    public notifyActionClosed(actionId: string) {
        this._collectionActionGroupsProvider.clearByActionId(actionId);
    }

    public registerModalDisplayPrevention(actionId: string, showCallback: (() => void) | undefined, scope?: any): (() => void) | undefined {
        const scriptActionHandler = this._actionHandlerManager.get(ActionType.Script) as InjectScriptActionHandler;
        return scriptActionHandler.registerDisplayPrevention(actionId, true, undefined, showCallback, () => { }, scope);
    }

    public registerNonModalDisplayPrevention(actionId: string, domElement: HTMLElement, showCallback: (() => void) | undefined, hideCallback: (() => void) | undefined, scope?: any): (() => void) | undefined {
        const scriptActionHandler = this._actionHandlerManager.get(ActionType.Script) as InjectScriptActionHandler;
        return scriptActionHandler.registerDisplayPrevention(actionId, false, domElement, showCallback, hideCallback, scope);
    }

    private _pushItem(item: ITrackingEvent): void {
        if (!this._initDone) {
            this._initQueue.push(item);
            return;
        }

        if (item.$command) {
            var engine: any = this;
            if (Engine.ApiCommands.indexOf(item.$command) == -1) {
                if (item.onready && Engine.isFunction(item.onready)) {
                    item.onready(<IApiCommandResult>{ success: false, error: "Command Not Supported" });
                }
                return;
            }
            const cmd: Function = engine[item.$command];
            const arg = <IApiCommandCallback<any>>{ onready: item.onready };
            cmd.apply(this, [arg]);
            return;
        }

        const sampling = this._localUserIdProvider.getSamplingData();
        const samplingOverride = this._getSamplingOverride();

        if (samplingOverride === undefined && sampling?.s === false && ((new Date().valueOf() - sampling.ts) < Engine.OneHour * 24))
            return;

        const rtdeRequest = this._parseItemData(item, samplingOverride);
        this.trackApiCall(RequestType.Rtde, rtdeRequest, this._clientApi.track_Post_1(rtdeRequest)).then(responseData => {
            this._handleRequestResponse(responseData);

            if (item.onflickeraction && Engine.isFunction(item.onflickeraction)) {

                let x = !!(responseData && responseData.actions && responseData.actions.length && responseData.actions.some(i => (i.conf && (<IssuedInjectScriptAction>i.conf).flickerDefendingRequired) == true));
                try {
                    item.onflickeraction(x);
                } catch {
                }
            }

            if (item && item.onready && Engine.isFunction(item.onready)) {
                try {
                    item.onready();
                }
                catch {

                }
            }
        });
    }

    private _sendHandlingState(data: UpdatedStateDebuggerData) {
        this._profileDebuggerBootstrap?.sendHandlingState(data);
    }

    private debuggerReady(): void {
        this._debuggerReady = true;

        if (this._debuggerApiCallsQueue) {
            for (var log of this._debuggerApiCallsQueue) {
                this._profileDebuggerBootstrap!.sendTrackingRequestResponse(log);
            }
        }
        this._debuggerApiCallsQueue = undefined;
    }

    private trackApiCall<TResponseData>(requestType: RequestType, data: any, apiRequest: ApiPromise<TResponseData>): Promise<TResponseData> {
        if (!this._debuggerEnabling) {
            // pass through, skip logging
            return apiRequest;
        }

        const onResponse = (responseData: TResponseData | undefined) => {
            const requestMetadata = apiRequest.requestMetadata!;
            const responseMetadata = apiRequest.responseMetadata!;

            const msg: ApiRequestResponseData = {
                request: {
                    metadata: {
                        date: requestMetadata.date,
                        type: requestType,
                        url: requestMetadata.url,
                        headers: requestMetadata.headers,
                        method: requestMetadata.method,
                        mode: requestMetadata.mode
                    },
                    data: data
                },
                response: {
                    metadata: {
                        date: responseMetadata.date,
                        ok: responseMetadata.ok,
                        status: responseMetadata.status,
                        headers: responseMetadata.headers,
                        type: responseMetadata.type
                    },
                    data: responseData
                }
            };
            this.notifyDebugger(msg);
        }

        return apiRequest
            .then(response => {
                onResponse(response);
                return response;
            }, reason => {
                onResponse(undefined);
                throw reason;
            });
    }

    private notifyDebugger(requestResponse: ApiRequestResponseData) {
        if (this._debuggerEnabling && !this._debuggerReady) {
            this._debuggerApiCallsQueue?.push(requestResponse);
            return;
        }

        if (this._profileDebuggerBootstrap && this._debuggerReady) {
            this._profileDebuggerBootstrap.sendTrackingRequestResponse(requestResponse);
        }
    }

    private notifyDebuggerDisabled() {
        this._debuggerEnabling = false;
        this._debuggerApiCallsQueue = undefined;
    }

    private notifyDebuggerEnabling() {
        if (this._debuggerEnabling || this._debuggerReady)
            return;

        this._debuggerApiCallsQueue = [];
        this._debuggerEnabling = true;
    }

    private _registerActionHanlers() {
        this._actionHandlerManager.register(ActionType.Cookie, new cookieActionHandler.CookieActionHandler(this._engine, this._logger));
        this._actionHandlerManager.register(ActionType.Overlay, new overlayActionHandler.OverlayActionHandlerProxy(this._engine, this._logger, this._collectionActionGroupsProvider));
        this._actionHandlerManager.register(ActionType.Script, new injectScriptActionHandler.InjectScriptActionHandler(this._engine, this._logger, this._collectionActionGroupsProvider));
        this._actionHandlerManager.register(ActionType.Html, new injectHTMLActionHandler.InjectHTMLActionHandler(this._engine, this._logger));
        this._actionHandlerManager.register(ActionType.Voucher, new overlayActionHandler.OverlayActionHandlerProxy(this._engine, this._logger, this._collectionActionGroupsProvider));
        this._actionHandlerManager.register(ActionType.Pixel, new pixelActionHandler.PixelActionHandler(this._engine, this._logger));

        this._handoverHandlerManager.register(DataExchangeType.Cookie, new cookieActionHandler.CookieActionHandler(this._engine, this._logger, true));
        this._handoverHandlerManager.register(DataExchangeType.Script, new injectScriptActionHandler.InjectScriptActionHandler(this._engine, this._logger, this._collectionActionGroupsProvider, true));
        this._handoverHandlerManager.register(DataExchangeType.Pixel, new pixelActionHandler.PixelActionHandler(this._engine, this._logger, true));
    }

    private _getActionHandler(actionType: ActionType): actionHandler.ActionHandler {
        return this._actionHandlerManager.get(actionType);
    }

    private _getHandoverHandler(dataExchangeType: DataExchangeType): actionHandler.ActionHandler {
        return this._handoverHandlerManager.get(dataExchangeType);
    }

    private _handleRequestResponse(responseData: TrackingResultFinal | null | undefined) {
        if (!responseData)
            return null;
        if (responseData.uIds && responseData.uIds.length > 0) {

            const userId = responseData.uIds[responseData.uIds.length - 1];
            const p = new Array<Promise<void>>();
            const userIdProviders = this._userIdProviderManager.getProviders();

            for (const providerName in userIdProviders) {
                p.push(userIdProviders[providerName].setUserId(userId.id!, userId.lifeTime));
            }

            this._realUserIdPromise.resolve(userId.id!);

            if (p.length > 0) {
                Promise.all(p).then(() => {
                    if (this._userIds)
                        this._userIds.forEach(x => x.id = userId.id);
                });

                if (this._userIds)
                    this._cachedResoveUserIdsPromise = Promise.resolve(this._userIds);
            }
        }

        this._localUserIdProvider.setSampling(responseData.sampling);

        if (responseData.actions && responseData.actions.length > 0) {

            for (let action of responseData.actions) {
                if (!(<any>action).type)
                    continue;

                let handler = this._actionHandlerManager.get((<any>action).type);
                if (handler)
                    handler.handle(action);
            }
        }

        if (responseData.handovers && responseData.handovers.length > 0) {

            for (let handover of responseData.handovers) {
                if (!handover.conf || !handover.conf.dataExchangeType)
                    continue;

                let handler = this._handoverHandlerManager.get(handover.conf.dataExchangeType);
                if (handler)
                    handler.handle(handover);
            }
        }
        return responseData;
    }

    private _unissuedAction(action: FinalIssuedAction) {
        if (!action.userId || !action.organizationId || !action.collectionId || !action.actionId)
            return;

        const conf = action.conf as (IssuedOverlayAction | IssuedInjectScriptAction);
        const obj: UnissuedActionBase = {
            actionCategory: action.actionCategory,
            itemId: conf.itemId,
            itemType: conf.itemType
        }

        this._clientApi.actionRevert_Post_3(action.userId, action.organizationId, action.collectionId, action.actionId, obj);
    }

    private _getExternalCookies(): ExternalCookieModel[] {
        let cookies = jscookie.get();
        let externalCookies: ExternalCookieModel[] = [];

        if (this._conf.adobeIdCookie)
            externalCookies.push.apply(externalCookies, this._getAdobeCookies(cookies));

        return externalCookies;
    }

    private _getAdobeCookies(cookies: any): ExternalCookieModel[] {
        let adobeExternalCookies: ExternalCookieModel[] = [];
        let cookieName = this._conf.adobeIdCookie!;
        try {
            if (cookies[cookieName]) {
                var m = (<string>cookies[cookieName]).match(/MCMID\|(\w+)/);
                if (m && m.length >= 2) {
                    adobeExternalCookies.push({
                        cookieName: cookieName,
                        system: 'Adobe Global',
                        value: m[1],
                        partnerId: this._conf.adobePartnerId,
                        partnerSystem: this._conf.adobePartnerSystem
                    });
                }
            }
        }
        catch (e) { }
        return adobeExternalCookies;
    }

    private _parsePayload(payload: any): any {
        let parsedPayload = payload;

        if (typeof payload === "string") {
            try {
                parsedPayload = JSON.parse(payload);
            } catch (e) {
                return payload;
            }
        }
        if (parsedPayload
            && typeof (parsedPayload) === "object"
            && parsedPayload.leadInformation
            && parsedPayload.leadInformation.additionalInformation
            && typeof parsedPayload.leadInformation.additionalInformation !== "string") {
            parsedPayload.leadInformation.additionalInformation = JSON.stringify(parsedPayload.leadInformation.additionalInformation);
        }
        return JSON.stringify(parsedPayload);
    }

    private _getClientSampling(): ClientSampling | undefined {
        let sampling = this._localUserIdProvider.getClientSampling();
        const samplingOverride = this._getSamplingOverride();

        if (samplingOverride !== undefined) {
            if (!sampling) {
                sampling = {};
            }
            sampling.override = samplingOverride;
        }
        return sampling;
    }

    private _parseItemData(item: ITrackingEvent, samplingOverride?: boolean): TrackEventData {
        //TODO set correct params and values in return

        let sampling = this._localUserIdProvider.getClientSampling();

        if (samplingOverride !== undefined) {
            if (!sampling) {
                sampling = {};
            }
            sampling.override = samplingOverride;
        }

        return {
            scriptVersion: Engine.Version,
            uIds: this._userIds,
            scriptId: this._conf.scriptId,
            event: item.event,
            eventObject: this._parsePayload(item.payload),
            url: window.location.href,
            urlReferrer: document.referrer,
            title: document.title,
            externalCookies: this._getExternalCookies(),
            timezoneOffset: (new Date().getTimezoneOffset() * -1),
            sampling: sampling
        };
    }

    private hasHrefValue(value: string): boolean {
        return window.location.href.indexOf(value) !== -1;
    }

    private _getSamplingOverride(): boolean | undefined {
        if (this.hasHrefValue('smart-signals-track=on'))
            return true;

        if (this.hasHrefValue('smart-signals-track=off'))
            return false;

        return undefined;
    }

    private _getDebuggerState(): DebuggerState | undefined {
        if (this.hasHrefValue('smart-signals-debugger=min') || this.hasHrefValue('smart-signals-console=min'))
            return DebuggerState.MIN;

        if (this.hasHrefValue('smart-signals-debugger=on') || this.hasHrefValue('smart-signals-console=on'))
            return DebuggerState.ON;

        if (this.hasHrefValue('smart-signals-debugger=off') || this.hasHrefValue('smart-signals-console=off'))
            return DebuggerState.OFF;

        return undefined;
    }

    private _setupCommunicationChannel() {
        if (window.addEventListener) {
            window.addEventListener("message", (evn) => this._onMessage(evn), false);
        }
    }

    private _onMessage(event: MessageEvent) {
        var data = event.data;
        if (data.source == 'SmasiEngineChannel' && data.func == "push" && data.data) {
            this.push.apply(this, data.data);
        }
    }

    public showActions(hideGroup?: string): void {
        if (--this._hideActionsCounter <= 0) {
            this._hideActionsCounter = 0;
            [this._actionHandlerManager.get(ActionType.Overlay), this._actionHandlerManager.get(ActionType.Voucher)]
                .forEach(i => (i as overlayActionHandler.OverlayActionHandlerProxy).showActions(hideGroup));
        }
    }

    public hideActions(hideGroup?: string): void {
        this._hideActionsCounter++;
        [this._actionHandlerManager.get(ActionType.Overlay), this._actionHandlerManager.get(ActionType.Voucher)]
            .forEach(i => (i as overlayActionHandler.OverlayActionHandlerProxy).hideActions(hideGroup));
    }

    private _getCurrentUrl(): string {
        return window.location.href;
    }

    private _getLoggerContext(): LogContext {
        let ctx: LogContext = {
            scriptId: this._conf?.scriptId,
        }

        if (this._userIds) {
            ctx.userId = this._userIds.find(x => x.type == UserIdProviderType.Local)?.id;
        }
        return ctx;
    }

    public getModule(name: 'vwFreespee'): Promise<VwFreespeeModule>
    public getModule(name: 'vwFreespee') {
        switch (name) {
            case 'vwFreespee':
                if (!this._modules.has(name)) {
                    this._modules.set(name, import(/* webpackChunkName: "vwFreespee" */ './Modules/vwFreespee').then(i => new i.VwFreespeeModule(this._engine, this._clientApi)));
                }
                return this._modules.get(name);

        }
    }

    public async issueExternalAction(externalActionId: string, aditionalData?: string | {}, userToken?: string): Promise<any> {
        let _aditionalData: string | undefined;

        if (aditionalData) {
            if (typeof aditionalData === "string") {
                _aditionalData = aditionalData;
            }
            else {
                try {
                    _aditionalData = JSON.stringify(aditionalData)
                } catch (e) {
                    console.warn("issueExternalAction: failed to parse 'aditionalData'");
                    throw e;
                }
            }
        }

        const userId = await this._getUserId(true);

        try {
            const requestData: ExternalActionClientModel = {
                userId: userId!,
                token: userToken,
                aditionalData: _aditionalData
            };

            return await this.trackApiCall(RequestType.ExternalAction, requestData, this._clientApi.externalAction_Post_4(externalActionId, requestData)).then(response => {
                return response.result;
            })

        } catch (e) {
            throw e;
        }
    }
}

function isEngine(engine: any) {
    return engine instanceof Engine || (engine && (<Engine>engine).scopeName === 'SmartSignals2');
};



let current: Engine | ITrackingEvent[] = (<any>window).smartSignals2;

(<any>window).smartSignals2 = isEngine(current) ? current as Engine : new Engine(current as ITrackingEvent[]);

